// Utils
import { isWebBridgeSupported, getPlatformType } from '@shopee-user/platform';
import { getDeviceFingerprint, getRiskToken } from '@shopee/web-bridge-sdk';

// Constants & Enums
import { PlatformType, config } from '@shopee-user/platform';

// Types
import { WithFirebasePerformanceProps } from '@shopee-user-common/firebase-performance-utils';

const isIOS = getPlatformType() === PlatformType.iOS;

enum TraceEvent {
  Failed = 'GetNativeDFPFailed',
  Success = 'GetNativeDFPSuccess',
  Skipped = 'GetNativeDFPSkipped',
}

// The JS-Native interaction could take time. Increase the timeout value to support slower devices
const CUSTOM_NATIVE_DFP_TIMEOUT = 200; // ms

/** Gets App device fingerprint from webbridge.
 * All ToB Apps should support these webbridge commands: getDeviceFingerprint(Android) or getRiskToken(iOS)
 *
 * If the command is not supported, getDeviceFingerprint will return with a maximum timeout of 100ms.
 * Min App versions
 *   - For Partner App, around v2.80 (Android), below v2.50 (iOS)
 *   - For SPXDriver App, TBC
 *
 * FE TD: https://confluence.shopee.io/x/hBo6Xw
 * */

export const getNativeDeviceFingerprint = async ({
  recordFirebasePerformance,
  getNativeVersion,
  isAndroidAppVersionNotSupported = false,
}: {
  recordFirebasePerformance?: WithFirebasePerformanceProps['recordFirebasePerformance'];
  getNativeVersion: () => string;
  isAndroidAppVersionNotSupported?: boolean;
}) => {
  if (!isWebBridgeSupported()) {
    return '';
  }

  const triggerFirebase = ({
    traceName,
    deviceType,
    errorMessage,
  }: {
    traceName: TraceEvent;
    deviceType: string;
    errorMessage?: string;
  }) => {
    recordFirebasePerformance &&
      recordFirebasePerformance({
        traceName: traceName,
        duration: performance.now(),
        options: {
          attributes: {
            deviceType,
            businessTarget: config.BUSINESS_TARGET,
            appVersion: getNativeVersion(),
            error: errorMessage || '',
          },
        },
      });
  };

  if (isIOS) {
    const response = await getRiskToken({ timeout: CUSTOM_NATIVE_DFP_TIMEOUT });

    if (response.error) {
      console.log('getRiskToken failed', response);
      triggerFirebase({
        traceName: TraceEvent.Failed,
        deviceType: PlatformType[PlatformType.iOS],
        errorMessage: response.errorMessage,
      });
      return '';
    }
    console.log('getRiskToken success:', response.data);
    triggerFirebase({
      traceName: TraceEvent.Success,
      deviceType: PlatformType[PlatformType.iOS],
    });
    return (response.data && response.data.riskToken) || '';
  } else {
    if (isAndroidAppVersionNotSupported) {
      console.log('Lower than min version, skipping getDeviceFingerprint');
      triggerFirebase({
        traceName: TraceEvent.Skipped,
        deviceType: PlatformType[PlatformType.Android],
      });
      return '';
    }

    const response = await getDeviceFingerprint({
      timeout: CUSTOM_NATIVE_DFP_TIMEOUT,
    });

    if (response.error) {
      console.log('getDeviceFingerprint failed', response);
      triggerFirebase({
        traceName: TraceEvent.Failed,
        deviceType: PlatformType[PlatformType.Android],
        errorMessage: response.errorMessage,
      });
      return '';
    }
    console.log('getDeviceFingerprint success:', response.data);
    triggerFirebase({
      traceName: TraceEvent.Success,
      deviceType: PlatformType[PlatformType.Android],
    });
    return (response.data && response.data.fingerprint) || '';
  }
};
